/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { inject, Injectable, OnDestroy } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { filter, takeUntil } from "rxjs/operators";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { Subject } from "rxjs";
import { EnvironmentService } from "@irian-ro/util-environment";

const SW_BROADCAST_CHANNEL = "deliwell-sw-update";
const SW_BROADCAST_ACTIVATED = "sw-activated";
const STALE_OVERLAY_ID = "deliwell-sw-stale-overlay";

@Injectable()
export class ServiceWorkerVersionControlService implements OnDestroy {
  private router = inject(Router);
  private swUpdate = inject(SwUpdate);


  private isServiceWorkerActive = inject(EnvironmentService).getEnvironmentProperty('serviceWorker');

  private timestamp = 0;

  private broadcastChannel: BroadcastChannel | null = null;

  private overlayCleanup: (() => void) | null = null;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  listenToRouterEvents() {
    if (!this.isServiceWorkerActive) {
      return;
    }

    this.initBroadcastChannel();
    this.listenForVersionReady();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => this.checkForUpdates()
      });
  }

  checkForUpdates() {
    if (!this.swUpdate.isEnabled) {
      return;
    }
    if (this.timestamp !== 0 && Date.now() - this.timestamp <= 1000) {
      return;
    }
    this.timestamp = Date.now();
    this.swUpdate.checkForUpdate();
  }

  private listenForVersionReady() {
    if (!this.swUpdate.isEnabled) {
      return;
    }
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.swUpdate.activateUpdate()
            .then(() => {
              this.broadcastChannel?.postMessage(SW_BROADCAST_ACTIVATED);
              this.showStaleOverlay();
            })
            .catch(err => console.error('SW activation failed', err));
        }
      });
  }

  private initBroadcastChannel() {
    if (typeof BroadcastChannel === 'undefined' || this.broadcastChannel) {
      return;
    }
    this.broadcastChannel = new BroadcastChannel(SW_BROADCAST_CHANNEL);
    this.broadcastChannel.onmessage = (event: MessageEvent) => {
      if (event.data === SW_BROADCAST_ACTIVATED) {
        this.showStaleOverlay();
      }
    };
  }

  // Renders a fixed, full-viewport overlay that blocks all UI except the reload button.
  // Idempotent: subsequent calls are no-ops while the overlay is mounted.
  private showStaleOverlay() {
    if (typeof document === 'undefined' || document.getElementById(STALE_OVERLAY_ID)) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.id = STALE_OVERLAY_ID;
    overlay.setAttribute('role', 'alertdialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', `${STALE_OVERLAY_ID}-title`);
    overlay.style.cssText =
      'position:fixed;inset:0;background:rgba(15,23,42,0.55);' +
      'z-index:2147483647;display:flex;align-items:center;justify-content:center;' +
      'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;';

    const dialog = document.createElement('div');
    dialog.style.cssText =
      'background:#fff;color:#1f2937;padding:28px 32px;border-radius:8px;' +
      'max-width:420px;width:90%;box-shadow:0 20px 50px rgba(0,0,0,0.35);text-align:center;';

    const title = document.createElement('h2');
    title.id = `${STALE_OVERLAY_ID}-title`;
    title.textContent = 'A new version is available';
    title.style.cssText = 'margin:0 0 12px;font-size:1.15rem;font-weight:600;';

    const message = document.createElement('p');
    message.textContent = 'This tab is running an older version. Reload to continue with the latest.';
    message.style.cssText = 'margin:0 0 20px;font-size:0.95rem;line-height:1.45;color:#4b5563;';

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Reload';
    button.style.cssText =
      'background:#2563eb;color:#fff;border:0;border-radius:6px;' +
      'padding:10px 24px;font-size:0.95rem;font-weight:600;cursor:pointer;';
    button.addEventListener('click', () => window.location.reload());

    dialog.append(title, message, button);
    overlay.appendChild(dialog);

    // Block keyboard activations on elements outside the dialog (mouse is already
    // blocked by the backdrop). Focus is trapped to the reload button.
    const keyBlocker = (e: KeyboardEvent) => {
      if (!dialog.contains(e.target as Node)) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    const focusTrap = (e: FocusEvent) => {
      if (!dialog.contains(e.target as Node)) {
        e.stopPropagation();
        button.focus();
      }
    };
    document.addEventListener('keydown', keyBlocker, true);
    document.addEventListener('keypress', keyBlocker, true);
    document.addEventListener('keyup', keyBlocker, true);
    document.addEventListener('focusin', focusTrap, true);

    document.body.appendChild(overlay);
    button.focus();

    this.overlayCleanup = () => {
      document.removeEventListener('keydown', keyBlocker, true);
      document.removeEventListener('keypress', keyBlocker, true);
      document.removeEventListener('keyup', keyBlocker, true);
      document.removeEventListener('focusin', focusTrap, true);
      overlay.remove();
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.broadcastChannel?.close();
    this.broadcastChannel = null;
    this.overlayCleanup?.();
    this.overlayCleanup = null;
  }

}
